import Fuse from "fuse.js";
import { stripDiacritics } from "../../../../../../../../../modules/assets/helperFunctions";
import { GroupMember } from "../../../../../../domain";
import { TeamOption } from "./DedicatedAutocomplete";

// ===================================================================
// VERSION 1: Original - For searching GroupMember only
// ===================================================================

const memberSearchOptions: Fuse.IFuseOptions<GroupMember> = {
  isCaseSensitive: false,
  findAllMatches: true,
  minMatchCharLength: 2,
  shouldSort: false,
  threshold: 0.2,
  keys: [
    {
      name: "First name",
      getFn: (member: GroupMember): string =>
        stripDiacritics(member.user.name.first),
    },
    {
      name: "Last name",
      getFn: (member: GroupMember): string =>
        stripDiacritics(member.user.name.last),
    },
    {
      name: "Full name",
      getFn: (member: GroupMember): string =>
        stripDiacritics(member.user.name.full),
    },
    {
      name: "Reversed full name",
      getFn: (member: GroupMember): string => {
        const name = member.user.name;
        const value = `${name.last} ${name.first}`;
        return stripDiacritics(value);
      },
    },
    {
      name: "Group name",
      getFn: (member: GroupMember): string => stripDiacritics(member.groupName),
    },
  ],
};

/**
 * Creates a Fuse instance for searching an array of GroupMember objects.
 */
export function createFuse(members: GroupMember[]): Fuse<GroupMember> {
  const index = Fuse.createIndex<GroupMember>(
    memberSearchOptions.keys!,
    members
  );
  return new Fuse<GroupMember>(members, memberSearchOptions, index);
}

// ===================================================================
// VERSION 2: New - For searching both GroupMember and TeamOption
// ===================================================================

type SearchableItem = GroupMember | TeamOption;

const dedicatedSearchOptions: Fuse.IFuseOptions<SearchableItem> = {
  isCaseSensitive: false,
  findAllMatches: true,
  minMatchCharLength: 2,
  shouldSort: false,
  threshold: 0.2,
  keys: [
    {
      name: "First name",
      getFn: (item: SearchableItem): string => {
        if ("isTeam" in item && item.isTeam) return ""; // Teams don't have a first name
        return stripDiacritics(item.user.name.full);
      },
    },
    {
      name: "Last name",
      getFn: (item: SearchableItem): string => {
        if ("isTeam" in item && item.isTeam) return ""; // Teams don't have a last name
        return stripDiacritics(item.user.name.full);
      },
    },
    {
      name: "Full name",
      getFn: (item: SearchableItem): string =>
        stripDiacritics(item.user.name.full),
    },
    {
      name: "Reversed full name",
      getFn: (item: SearchableItem): string => {
        if ("isTeam" in item && item.isTeam) return "";
        const name = item.user.name;
        const value = `${name.full}`;
        return stripDiacritics(value);
      },
    },
    {
      name: "Group name",
      getFn: (item: SearchableItem): string => stripDiacritics(item.groupName),
    },
  ],
};

/**
 * Creates a Fuse instance for searching a mixed array of GroupMember and TeamOption objects.
 */
export function createDedicatedSearchFuse(
  items: SearchableItem[]
): Fuse<SearchableItem> {
  const index = Fuse.createIndex<SearchableItem>(
    dedicatedSearchOptions.keys!,
    items
  );
  return new Fuse<SearchableItem>(items, dedicatedSearchOptions, index);
}
